import { ReactElement, useContext, useMemo } from "react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { faChartPie, faClipboardList } from "@fortawesome/free-solid-svg-icons"
import { Loading } from "@wfp/ui"

import { useGetReportObsQuery } from "services/marketprices/commodityPrice"
import { DataUploadContext } from "../../../../providers/DataUploadContext"

import { useGetReportByIdQuery } from "../../../../services/marketprices/reports"

import { defaultOutlierFilters } from "../../../../constants/validation"
import { formatDate } from "../../../../utils/helpers"
import { detectOutliers } from "../../../../utils/validation"

import { ObservationStatus, ReportData } from "../../../../interfaces/markets"

import StatusChart from "./StatusChart"
import OutliersChart from "./OutliersChart"

const initialData: ReportData = {
  validCount: 0,
  invalidKeysCount: 0,
  invalidSeriesCount: 0,
  invalidDateCount: 0,
  duplicatedCount: 0,
  discardedCount: 0,
  commodities: {} as Record<number, boolean>,
  markets: {} as Record<number, boolean>,
  approvedGroups: {} as Record<string, any[]>,
}

const initialOutliersData = {
  isOutlier: 0,
  normal: 0,
}

const Finalize = (): ReactElement => {
  const { filters, obsWithOutliers } = useContext(DataUploadContext)

  const { data: reportData, isFetching: isLoadingReport } = useGetReportByIdQuery(filters.reportId ?? 0, {
    skip: !filters.reportId,
  })
  const { data: reportObs, isFetching: isLoadingReportObs } = useGetReportObsQuery(filters.reportId ?? 0, {
    skip: !filters.reportId,
  })

  const data = useMemo(() => {
    if (!reportObs) return { ...initialData }

    return reportObs?.reduce(
      (acc, item) => {
        if (item.observationStatusId === ObservationStatus.VALID) acc.validCount++
        if (item.observationStatusId === ObservationStatus.INVALID_KEYS) acc.invalidKeysCount++
        if (item.observationStatusId === ObservationStatus.INVALID_SERIES) acc.invalidSeriesCount++
        if (item.observationStatusId === ObservationStatus.INVALID_DATE) acc.invalidDateCount++
        if (item.observationStatusId === ObservationStatus.DUPLICATED) acc.duplicatedCount++
        if (item.observationStatusId === ObservationStatus.DISCARDED) acc.discardedCount++

        if (item.commodityId !== null) acc.commodities[item.commodityId] = true
        if (item.marketId !== null) acc.markets[item.marketId] = true

        if (item.observationStatusId === ObservationStatus.APPROVED) {
          const dataCollectionFrequencyId = reportData?.dataCollectionFrequencyId ?? "unknown"
          const seriesCombinationCode = item.seriesCombinationCode ?? "unknown"
          const groupKey = `${dataCollectionFrequencyId}_${seriesCombinationCode}`

          if (!acc.approvedGroups[groupKey]) {
            acc.approvedGroups[groupKey] = []
          }
          acc.approvedGroups[groupKey].push(item)
        }

        return acc
      },
      { ...initialData },
    )
  }, [reportObs, reportData])

  const dataWithOutliers = useMemo(() => {
    if (!obsWithOutliers?.length) return { ...initialOutliersData }

    return detectOutliers(
      obsWithOutliers?.filter(
        item =>
          item.observationStatusId === ObservationStatus.VALID ||
          item.observationStatusId === ObservationStatus.APPROVED,
      ),
    )?.reduce(
      (acc, item) => {
        const isOutlier =
          item.isOutlier ||
          (item.MoM !== undefined && Math.abs(item.MoM ?? 0) >= defaultOutlierFilters.MoM) ||
          (item.YoY !== undefined && Math.abs(item.YoY ?? 0) >= defaultOutlierFilters.YoY)

        if (isOutlier) acc.isOutlier++
        else acc.normal++

        return acc
      },
      { ...initialOutliersData },
    )
  }, [obsWithOutliers])

  if (isLoadingReport || isLoadingReportObs)
    return (
      <div className="min-h-[50vh] w-full flex justify-center items-center">
        <Loading small withOverlay={false} />
      </div>
    )

  return (
    <>
      <p className="text-sm text-gray-500 mb-2">
        Validation complete. Review the summary below and check for any remaining issues. Correct errors before
        finalizing. When ready, click Save to proceed.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-x-2 gap-y-6 w-full max-w-[1000px] 2xl:max-w-[1200px] mx-auto items-center">
        <div className="min-w-[340px] border border-gray-200 rounded-lg bg-white border-solid mx-auto min-h-[450px]">
          <div className="text-lg font-bold mb-3 p-4 border-b border-gray-200 border-solid text-[var(--interactive-01)]">
            <FontAwesomeIcon icon={faClipboardList} className="mr-2" size="lg" color="var(--interactive-01)" />
            <span>Report Summary</span>
          </div>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm p-4 pt-0 pb-2"
            style={{ gridTemplateColumns: "auto 1fr" }}
          >
            <div className="text-gray-600">ID:</div>
            <div className="font-semibold">{reportData?.reportId}</div>

            <div className="text-gray-600">Country:</div>
            <div className="font-semibold truncate w-54" title={reportData?.countryName}>
              {reportData?.countryName}
            </div>

            <div className="text-gray-600">Mapping Schema:</div>
            <div className="font-semibold truncate w-54" title={reportData?.mappingSchemaName}>
              {reportData?.mappingSchemaName}
            </div>

            <div className="text-gray-600">Created by:</div>
            <div className="font-semibold truncate w-54" title={reportData?.uploadedByUserName}>
              {reportData?.uploadedByUserName}
            </div>

            <div className="text-gray-600">Created date:</div>
            <div className="font-semibold">{formatDate(reportData?.reportCreateDate)}</div>

            <div className="col-span-full my-0.5" />

            <div className="text-gray-600">Valid Records:</div>
            <div className="font-semibold text-teal-600">{data.validCount}</div>

            <div className="text-gray-600">Unknown Items:</div>
            <div className="font-semibold text-red-600">{data.invalidKeysCount}</div>

            <div className="text-gray-600">Series Not Enabled:</div>
            <div className="font-semibold text-red-600">{data.invalidSeriesCount}</div>

            <div className="text-gray-600">Invalid Dates:</div>
            <div className="font-semibold text-red-600">{data.invalidDateCount}</div>

            <div className="text-gray-600">Duplicate Records:</div>
            <div className="font-semibold text-red-600">{data.duplicatedCount}</div>

            <div className="text-gray-600">Discarded Records:</div>
            <div className="font-semibold text-red-600">{data.discardedCount}</div>

            <div className="col-span-full my-0.5" />

            <div className="text-gray-600">Commodities Count:</div>
            <div className="font-semibold text-[var(--interactive-01)]">{Object.keys(data.commodities)?.length}</div>

            <div className="text-gray-600">Markets Count:</div>
            <div className="font-semibold text-[var(--interactive-01)]">{Object.keys(data.markets)?.length}</div>

            {!!Object.keys(data.approvedGroups)?.length && (
              <>
                <div className="col-span-full my-1" />
                <div className="text-gray-600">Updated Approved Series:</div>
                <div className="font-semibold text-[var(--interactive-01)]">
                  {Object.keys(data.approvedGroups)?.length}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="min-w-[340px] border border-gray-200 rounded-lg bg-white border-solid mx-auto min-h-[470px]">
          <div className="text-lg font-bold p-4 mb-3 border-b border-gray-200 border-solid text-[var(--interactive-01)]">
            <FontAwesomeIcon icon={faChartPie} className="mr-2" size="lg" color="var(--interactive-01)" />
            <span>Observations Status</span>
          </div>
          <StatusChart data={data} />
        </div>

        <div className="min-w-[340px] border border-gray-200 rounded-lg bg-white border-solid mx-auto min-h-[470px]">
          <div className="text-lg font-bold p-4 border-b border-gray-200 border-solid text-[var(--interactive-01)]">
            <FontAwesomeIcon icon={faChartPie} className="mr-2" size="lg" color="var(--interactive-01)" />
            <span>Price Outliers</span>
          </div>
          <p className="text-xs p-2 text-gray-500 w-full text-center">
            MoM: {defaultOutlierFilters.MoM}% - YoY: {defaultOutlierFilters.YoY}%
          </p>

          <OutliersChart data={dataWithOutliers} />
        </div>
      </div>
    </>
  )
}

export default Finalize
